class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.close();
      }
    });

    // Checkout button loading state. Delegated (the footer re-renders on cart
    // updates, so a direct listener would be lost). On the first click we show a
    // spinner and let the native submit proceed to /checkout; subsequent clicks
    // are swallowed so the ~1-2s redirect can't be re-triggered (the "re-click"
    // pattern from session recordings). We must NOT disable the button here —
    // disabling a submit button in its own click handler cancels the submit.
    this.addEventListener('click', (event) => {
      const checkoutBtn = event.target.closest('#CartDrawer-Checkout, .cart__checkout-button');
      if (!checkoutBtn || checkoutBtn.disabled) return;
      if (checkoutBtn.dataset.loading === 'true') {
        event.preventDefault();
        return;
      }
      checkoutBtn.dataset.loading = 'true';
      checkoutBtn.classList.add('cart__checkout-button--loading');
      checkoutBtn.setAttribute('aria-busy', 'true');
    });

    // Reset the loading state if the user returns via back/forward (bfcache),
    // otherwise the button would still show a spinner.
    window.addEventListener('pageshow', () => {
      const btn = this.querySelector('#CartDrawer-Checkout');
      if (!btn) return;
      btn.dataset.loading = 'false';
      btn.classList.remove('cart__checkout-button--loading');
      btn.removeAttribute('aria-busy');
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement || null);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // renderContents only ever runs after an add — the cart is not empty.
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerHTML = parsedState.sections['cart-drawer'];
    const itemsTarget = this.querySelector('#CartDrawer-CartItems');
    const hadItemsList = !!(itemsTarget && itemsTarget.querySelector('.cart-items tbody'));

    if (drawerHTML && hadItemsList) {
      // Drawer is already showing items — update fragments in place so
      // unchanged product rows (and their images) don't flash on add.
      const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const itemsFresh = doc.querySelector('#CartDrawer-CartItems');
      if (itemsFresh) {
        if (window.morphCartItemsContents) {
          window.morphCartItemsContents(itemsTarget, itemsFresh.innerHTML);
        } else {
          itemsTarget.innerHTML = itemsFresh.innerHTML;
        }
      }
      // These fragments genuinely change on add (totals, rewards, suggestions).
      ['#cart-reward-bar', '.cart-drawer__footer', '.cart-drawer__upsell-section', '.cart-drawer__recommendations-section'].forEach(
        (selector) => {
          const target = this.querySelector(selector);
          const fresh = doc.querySelector(selector);
          if (target && fresh) target.replaceWith(fresh);
        }
      );
    } else if (drawerHTML) {
      // Empty → non-empty (or unexpected structure): swap the whole drawer.
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) cartDrawerEl.innerHTML = this.getSectionInnerHTML(drawerHTML, '#CartDrawer');
    }

    const bubbleHTML = parsedState.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) bubble.innerHTML = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // On a quantity change, re-render only the fragments that actually change —
  // the item rows and the footer totals. Leaving the reward bar, upsell and
  // recommendations untouched avoids replaying their entry animations (the
  // cause of the per-click flicker) and cuts the DOM work. The reward bar is
  // updated smoothly in place by cart-rewards.js. (Adding a product from the
  // PDP still does a full drawer render via CartDrawer.renderContents.)
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
